import './common.scss';
import './claim-entity.detail.financials.scss';
import './claim-diary.scss';

import { Box, Tab, Tabs } from '@mui/material';
import {
  Entity,
  GetClaimQuoteQueryQuery,
  ReserveInput,
  ReservePaymentInput,
  // useUpsertReserveMutation,
} from '@converge/graphql';
import { useMemo, useState } from 'react';

import { ClaimEntityPageContainer } from './claim-entity.page.container';

import { PaperLayout } from '../../../presentation';
import { PaymentHistoryTab } from './components/payment-history-tab';
import { RecoveryTab } from './components/recovery-tab';
import { ReservesTab } from './components/reserves-tab';
import { TabPanel } from './components/tab-panel';
import { useConvergeSnackBar } from '../../../contexts';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useOutletContext } from 'react-router-dom';

export type CreateReserveFields = ReserveInput;
export type CreatePaymentFields = ReservePaymentInput;

export interface IClaimProps {
  claimEntity: Entity;
  claimUuid: string;
  claimQuoteData: GetClaimQuoteQueryQuery;
  claimSubmissionDetails?: any;
}

export const ClaimEntityDetailFinancials = () => {
  const { claimUuid } = useOutletContext<IClaimProps>();

  return <ClaimEntityDetailFinancialsInner claimUuid={claimUuid} />;
};

interface MockRecovery {
  id: string;
  recoveryType: string;
  recoverySource: string;
  expectedAmount: number;
  recoveredAmount: number;
  recoveryDate: string | null;
  status: string;
  notes: string;
}

export const ClaimEntityDetailFinancialsInner = ({
  claimUuid,
}: {
  claimUuid: string;
}) => {
  // const { upsertReserve, loading } = useUpsertReserveMutation();
  const [activeTab, setActiveTab] = useState(0);
  const { notify } = useConvergeSnackBar();

  // Helper function to format dollar amounts
  const formatDollarAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Mock recovery data
  const mockRecoveryData = useMemo<MockRecovery[]>(
    () => [
      {
        id: 'recovery-1',
        recoveryType: 'Subrogation',
        recoverySource: 'Third Party Insurance',
        expectedAmount: 75000,
        recoveredAmount: 50000,
        recoveryDate: '2025-06-25',
        status: 'Partial',
        notes: 'Received partial payment, remainder expected by end of quarter',
      },
      {
        id: 'recovery-2',
        recoveryType: 'Salvage',
        recoverySource: 'Asset Liquidation',
        expectedAmount: 15000,
        recoveredAmount: 15000,
        recoveryDate: '2025-06-10',
        status: 'Complete',
        notes: 'Full recovery achieved through asset sale',
      },
    ],
    []
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleAddPayment = (reserveId: string) => {};

  const createReserve = () => {
    notify('Create reserve functionality will be implemented', {
      variant: 'info',
    });
  };

  // Render the main component
  return (
    <ClaimEntityPageContainer className="dairy-container" title="Financials">
      <PaperLayout>
        <Box
          className="financials-container"
          sx={{
            position: 'relative',
            height: activeTab === 2 ? '62vh' : 'calc(100vh - 300px)',
            overflow: 'auto',
          }}
        >
          {/* Tab navigation */}
          <Box
            className="tab-header"
            sx={{
              position: 'fixed',
              top: '290px',
              zIndex: 2,
              width: '92.5%',
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="financial tabs"
              sx={{
                fontWeight: 'bold',
                color: '#000000 !important',
                borderBottom: '1px solid #000000',
                width: '83.5% !important',
                backgroundColor: 'white',
                marginBottom: '10px',
                marginLeft: '1.5rem',
                '& .Mui-selected': {
                  fontWeight: 'bold',
                },
              }}
              variant="fullWidth"
            >
              <Tab
                sx={{
                  textTransform: 'none',
                  fontSize: '16px',
                  color: '#000000 !important',
                }}
                label="Reserves"
                id="financial-tab-0"
              />
              <Tab
                sx={{
                  textTransform: 'none',
                  fontSize: '16px',
                  color: '#000000 !important',
                }}
                label="Recovery"
                id="financial-tab-1"
              />
              <Tab
                sx={{
                  textTransform: 'none',
                  fontSize: '16px',
                  color: '#000000 !important',
                }}
                label="Payment History"
                id="financial-tab-2"
              />
            </Tabs>
          </Box>

          {/* Tab content */}
          <Box sx={{ mt: 2 }}>
            <TabPanel value={activeTab} index={0}>
              <ReservesTab
                formatDollarAmount={formatDollarAmount}
                createReserve={createReserve}
                handleAddPayment={handleAddPayment}
                loading={false}
                claimUuid={claimUuid}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <RecoveryTab
                mockRecoveryData={mockRecoveryData}
                formatDollarAmount={formatDollarAmount}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <PaymentHistoryTab
                formatDollarAmount={formatDollarAmount}
                claimUuid={claimUuid}
              />
            </TabPanel>
          </Box>
        </Box>
      </PaperLayout>
    </ClaimEntityPageContainer>
  );
};
